import { ReactElement, ReactNode, useState, useEffect } from "react";
import { Stack, Button, Tooltip, Typography } from "@altimateai/lego";
import { IconInfoCircle, IconX } from "@tabler/icons-react";
import quickstartBg from "@assets/images/quickstart-head-bg.png";

interface DismissibleAlertBannerProps {
  /** Unique identifier for localStorage key */
  bannerId: string;
  /** Optional heading text */
  title?: string;
  /** Main content text or ReactNode */
  content: string | ReactNode;
  /** Custom CSS classes */
  className?: string;
  /** Custom close button tooltip */
  closeTooltip?: string;
  /** Show info icon */
  showIcon?: boolean;
  /** Callback when banner is closed */
  onClose?: () => void;
}

export function DismissibleAlertBanner({
  bannerId,
  title,
  content,
  className = "al-bg-cover al-bg-no-repeat al-px-4 al-py-2 al-rounded-2xl al-text-white al-items-center",
  closeTooltip = "Close this banner",
  showIcon = true,
  onClose,
}: DismissibleAlertBannerProps): ReactElement | null {
  const [isVisible, setIsVisible] = useState(false);
  const localStorageKey = `dismissible-banner-${bannerId}`;

  useEffect(() => {
    // Check if banner was previously dismissed
    try {
      const isDismissed = localStorage.getItem(localStorageKey) === "true";
      setIsVisible(!isDismissed);
    } catch (error) {
      // If localStorage is not available, show the banner
      setIsVisible(true);
    }
  }, [localStorageKey]);

  const handleClose = () => {
    // Store dismissal state in localStorage
    try {
      localStorage.setItem(localStorageKey, "true");
    } catch (error) {
      // If localStorage is not available, continue anyway
      console.warn("Could not save banner dismissal state to localStorage:", error);
    }
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Stack
      className={className}
      style={{ backgroundImage: `url(${quickstartBg})` }}
      align="center"
      gap="4xl"
      aria-label="Info banner"
    >
      {showIcon && <IconInfoCircle aria-label="Info icon" className="al-shrink-0" />}
      <Stack direction="column" gap="sm" className="al-flex-1 al-py-2">
        {title && (
          <Typography variant="h4">
            {title}
          </Typography>
        )}
        {content && (
          <Typography variant="body" size="sm">
            {content}
          </Typography>
        )}
      </Stack>
      <Tooltip title={closeTooltip}>
        <Button
          className="hover:al-bg-transparent"
          variant="ghost"
          size="icon"
          title=""
          onClick={handleClose}
          aria-label="Close banner"
        >
          <IconX className="al-w-4 al-h-4" />
        </Button>
      </Tooltip>
    </Stack>
  );
}
